import { TypeIcon } from "../components/common/TypeIcon";
import { usePageBar } from "../contexts/PageBarContext";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  deleteField,
  getDoc,
  setDoc,
  writeBatch,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useSegments } from "../hooks/useSegments";
import { useInteractions } from "../hooks/useInteractions";
import { useEntityMap } from "../hooks/useEntityMap";
import { useLiveWaitTime } from "../hooks/useLiveWaitTime";
import { liveWaitToPrefill } from "../utils/liveWaitPrefill";
import { buildNestedSegmentOptions } from "../utils/segmentTree";
import {
  activityAncestors,
  buildNestedActivityOptions,
  descendantActivityIds,
} from "../utils/activityTree";
import { deleteActivityLiftingChildren, moveActivity } from "../utils/activityLogging";
import { ActivityList } from "../components/itinerary/ActivityList";
import { LogActivitySheet } from "../components/interactions/LogActivitySheet";
import { LogActivityPicker } from "../components/interactions/LogActivityPicker";
import { EventTimeline } from "../components/interactions/EventTimeline";
import { ActivityHero, type ActivityStatus } from "../components/interactions/ActivityHero";
import { StepOrderButton } from "../components/entities/StepOrderButton";
import { getEventOptionsForEntity } from "../config/defaultEventOptions";
import { OrdersEditor } from "../components/interactions/OrdersEditor";
import { TriviaEditor } from "../components/interactions/TriviaEditor";
import { ActivityPeople } from "../components/interactions/PeopleEditor";
import type { RoleListScope } from "../components/interactions/PeopleEditor";
import { parseRoleList, resolveActivityRoles } from "../config/peopleRoleTypes";
import type { RoleTypeOption } from "../config/peopleRoleTypes";
import { useTypeSettings } from "../contexts/TypeSettingsContext";
import { useAuth } from "../contexts/AuthContext";
import { WhoWasThere } from "../components/common/WhoWasThere";
import { LinkPlacePanel } from "../components/interactions/LinkPlacePanel";
import { RichTextEditor } from "../components/common/RichTextEditor";
import type { Interaction, InteractionEvent, Entity, EventType, EntityType, Person, Trip, DiningOrder, LoungeRound, TriviaQuestion, TravelPartyMember } from "../types";
import { getInteractionEntityType, interactionUsesOrders, defaultUsesRounds, interactionShowsTrivia, CUSTOM_INTERACTION_TYPES, normalizeEntityType, withCurrentType } from "../types";
import { deviceZone, activityStart, activityZone, segmentDayLookup, segmentZoneLookup, segmentLogSeeds, zoneNote, getActivityTimeRange, entityTypeLabel, calcTotalDelayTime, calcOrderToReceivedTime, interactionTimelineEvents, formatDuration, isActivityActive, expectedExpressWait } from "../utils/tripDisplay";
import { flightTiming, formatLateness, showTiming, showtimeChoices } from "../utils/scheduledTimes";
import { inheritedActivityParticipants, segmentParticipantsLookup } from "../utils/participants";
import { fireWrite } from "../utils/offlineWrites";
import { setActivitiesNoPlace, unlinkInteraction } from "../utils/entityLinking";
import { deepEqual, withoutUndefined } from "../utils/editDiff";
import { onSnapshotResilient } from "../utils/resilientSnapshot";
import { CustomQuestionInputs } from "../components/interactions/CustomQuestionInputs";
import { EntityPicker } from "../components/entities/EntityPicker";
import { FROM_AIRPORT_KEY, TO_AIRPORT_KEY, flightRoute, flightRouteLabel, isFlightActivity, titleWithRoute } from "../utils/flightRoute";
import { customAnswer, customAnswerValue } from "../utils/customQuestions";
import { resolveCustomQuestions, resolveTimeInputMode } from "../config/typeSettings";
import type { CustomQuestion } from "../types";

type TimerRef = { current: ReturnType<typeof setTimeout> | null };

/** A debounced live-editor save that hasn't been sent yet. */
interface PendingWrite {
  docId: string;
  payload: Record<string, unknown>;
  context: string;
}

/**
 * Send one editor's pending save now, if it has one. Keyed by the editor's
 * save timer, which is what identifies the editor.
 */
function sendPendingWrite(
  pending: Map<TimerRef, PendingWrite>,
  key: TimerRef,
  sent?: Map<TimerRef, PendingWrite>,
): void {
  const write = pending.get(key);
  if (!write) return;
  pending.delete(key);
  sent?.set(key, write);
  fireWrite(
    updateDoc(doc(db, "interactions", write.docId), {
      ...write.payload,
      updatedAt: serverTimestamp(),
    }),
    write.context,
  );
}

/**
 * Whether a snapshot's `value` at `path` is just this screen's own last write
 * to it coming back — see `sentWrites`.
 */
function isOwnEcho(
  sent: Map<TimerRef, PendingWrite>,
  key: TimerRef,
  docId: string,
  path: string,
  value: unknown,
): boolean {
  const write = sent.get(key);
  if (!write || write.docId !== docId || !(path in write.payload)) return false;
  return deepEqual(write.payload[path] ?? null, value ?? null);
}

/** What the edit form opened with — a save writes only what differs from it. */
interface EditFormValues {
  name: string;
  segmentId: string | null;
  parentId: string | null;
  entityType: EntityType | "";
  orders: DiningOrder[];
  rounds: LoungeRound[];
  useRounds: boolean;
  triviaQuestions: TriviaQuestion[];
  triviaScore: string;
  triviaWinningScore: string;
  postedWait: string;
  scheduledTime: string;
  scheduledBoarding: string;
}

export function InteractionDetail() {
  const { tripId, interactionId } = useParams<{ tripId: string; interactionId: string }>();
  const navigate = useNavigate();
  const [interaction, setInteraction] = useState<Interaction | null>(null);
  // Which doc `interaction` / `entity` were read from. The page is reused from
  // one activity to the next, so for a moment after the URL changes they can
  // still hold the previous activity's — see `liveWaitToPrefill`.
  const [loadedInteractionId, setLoadedInteractionId] = useState<string | null>(null);
  const [loadedEntityId, setLoadedEntityId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [editSegmentId, setEditSegmentId] = useState<string | null>(null);
  // What this activity was logged inside of — the park check-in that holds the
  // rides, the airport that holds the lounge. See `utils/activityTree`.
  const [editParentId, setEditParentId] = useState<string | null>(null);
  // Logging inside navigates to the new child, and this page is reused for it,
  // so the sheet's open flag would carry over and leave it covering the
  // activity just created. Close it whenever the activity changes.
  const [loggingInside, setLoggingInside] = useState(false);
  const [sheetForId, setSheetForId] = useState(interactionId);
  if (sheetForId !== interactionId) {
    setSheetForId(interactionId);
    setLoggingInside(false);
  }
  // Type for entity-less ("custom") interactions — lets you type an untyped
  // activity, or fix a wrong type. Ignored when a real entity is linked.
  const [editEntityType, setEditEntityType] = useState<EntityType | "">("");
  const [entity, setEntity] = useState<Entity | null>(null);
  const { userProfile } = useAuth();
  const canManageEntities = !!userProfile?.roles?.includes("manage_entities");
  // The activity type's settings as saved in the app — its default steps,
  // time input, questions and staff roles, under whatever its place sets.
  const typeSettings = useTypeSettings(
    getInteractionEntityType(entity, interaction?.customResponses as Record<string, unknown> | undefined),
  );
  const [entityLoading, setEntityLoading] = useState(false);
  const [trip, setTrip] = useState<Trip | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // The "give this activity a real place" panel. Off by default: it loads the
  // catalog and the trip's other activities, which only curation needs.
  const [linkingPlace, setLinkingPlace] = useState(false);
  // Bumped by the ⋮ menu's "Add person" to open the staff & crew form in place.
  const [addPersonSignal, setAddPersonSignal] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const { segments } = useSegments(tripId);
  // The trip's other activities: what's logged inside this one, what it sits
  // inside of, and what it could be moved under.
  const { interactions: tripInteractions } = useInteractions(tripId);

  // Orders edit state (unified)
  const [editOrders, setEditOrders] = useState<DiningOrder[]>([]);
  const [editRounds, setEditRounds] = useState<LoungeRound[]>([]);
  const [editUseRounds, setEditUseRounds] = useState(false);

  // Trivia edit state
  const [editTriviaQuestions, setEditTriviaQuestions] = useState<TriviaQuestion[]>([]);
  const [editTriviaScore, setEditTriviaScore] = useState("");
  const [editTriviaWinningScore, setEditTriviaWinningScore] = useState("");

  // Posted wait / scheduled time edit state
  const [editPostedWait, setEditPostedWait] = useState("");
  const [editScheduledTime, setEditScheduledTime] = useState("");
  const [editScheduledBoarding, setEditScheduledBoarding] = useState("");

  // The edit form's values as it opened — what a save diffs against.
  const editOpened = useRef<EditFormValues | null>(null);

  // Live-editor saves waiting out their debounce, keyed by the editor's save
  // timer. An editor with one pending is mid-edit: incoming snapshots leave its
  // value alone. Every other live value follows the snapshot, so what other
  // people (and the watch) change shows up here instead of being overwritten by
  // this screen's stale copy on its next save. Flushed on unmount, so an edit
  // made just before tapping Back still lands.
  const pendingWrites = useRef(new Map<TimerRef, PendingWrite>());
  // The last write each editor sent. Its own write comes straight back as a
  // snapshot, in the stored form (trimmed, a number rather than "1."), and
  // re-seeding the input from that would rewrite what is still being typed:
  // "Space " + pause + "Mountain" became "SpaceMountain". A snapshot that just
  // echoes this is skipped; one that differs is someone else's edit.
  const sentWrites = useRef(new Map<TimerRef, PendingWrite>());

  // Live (always-on) notes editing with debounced auto-save
  const [liveNotes, setLiveNotes] = useState("");
  const [notesStatus, setNotesStatus] = useState<"idle" | "saving" | "saved">("idle");
  const notesSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live posted-wait editing with debounced auto-save (mirrors notes pattern)
  const [livePostedWait, setLivePostedWait] = useState("");
  const [postedWaitStatus, setPostedWaitStatus] = useState<"idle" | "saving" | "saved">("idle");
  const postedWaitSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const postedWaitSavedStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live location/venue editing (e.g. "Spinnaker Lounge, Deck 15"), debounced
  // auto-save — stored on customResponses.location.
  const [liveLocation, setLiveLocation] = useState("");
  const [locationStatus, setLocationStatus] = useState<"idle" | "saving" | "saved">("idle");
  const locationSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const locationSavedStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live scheduled-time editing (entities in scheduled_time mode — showtimes,
  // flight departure, etc.), debounced auto-save.
  const [liveScheduledTime, setLiveScheduledTime] = useState("");
  const [scheduledTimeStatus, setScheduledTimeStatus] = useState<"idle" | "saving" | "saved">("idle");
  const scheduledTimeSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduledTimeSavedStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A flight's boarding time, beside its departure (`scheduled_time`).
  const [liveScheduledBoarding, setLiveScheduledBoarding] = useState("");
  const [scheduledBoardingStatus, setScheduledBoardingStatus] = useState<"idle" | "saving" | "saved">("idle");
  const scheduledBoardingSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduledBoardingSavedStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live orders/rounds editing with debounced auto-save — lets the user add an
  // order or round straight from the detail view without entering edit mode.
  const [liveOrders, setLiveOrders] = useState<DiningOrder[]>([]);
  const [liveRounds, setLiveRounds] = useState<LoungeRound[]>([]);
  const [liveUseRounds, setLiveUseRounds] = useState(false);
  const [ordersStatus, setOrdersStatus] = useState<"idle" | "saving" | "saved">("idle");
  const liveOrdersInitialized = useRef(false);
  const roundsDefaultApplied = useRef(false);
  const ordersSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ordersSavedStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live trivia editing (add/score questions straight from the detail view),
  // debounced auto-save — mirrors the orders pattern.
  const [liveTriviaQuestions, setLiveTriviaQuestions] = useState<TriviaQuestion[]>([]);
  const [liveTriviaScore, setLiveTriviaScore] = useState("");
  const [liveTriviaWinningScore, setLiveTriviaWinningScore] = useState("");
  const [triviaStatus, setTriviaStatus] = useState<"idle" | "saving" | "saved">("idle");
  const triviaSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triviaSavedStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live "who was here" (trip travel party) editing. An absent or empty
  // participants field means "not set", and falls back to the group the
  // activity's segment carries (and ultimately the whole party); the explicit
  // list is only written once the user deselects someone here.
  const [liveParticipants, setLiveParticipants] = useState<string[] | null>(null);
  const [participantsStatus, setParticipantsStatus] = useState<"idle" | "saving" | "saved">("idle");
  const participantsSavedStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live "access tier" (the entity's activityOptions — e.g. Lightning Lane vs
  // Standby, Quick Service vs Sit-down). Pick one of the place's options; the
  // chosen option id is stored on interaction.accessTierId.
  const [liveAccessTier, setLiveAccessTier] = useState<string | null>(null);
  const [accessTierStatus, setAccessTierStatus] = useState<"idle" | "saving" | "saved">("idle");
  const accessTierSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const accessTierSavedStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live answers to the place's own questions (the song on Guardians, the
  // booth at the festival stand), each debounced and saved on its own under
  // customResponses[question.id]. Keyed by question id.
  const [liveAnswers, setLiveAnswers] = useState<Record<string, string>>({});
  const [answerStatus, setAnswerStatus] = useState<Record<string, "idle" | "saving" | "saved">>({});
  const answerTimers = useRef<
    Record<string, { save: { current: ReturnType<typeof setTimeout> | null }; saved: { current: ReturnType<typeof setTimeout> | null } }>
  >({});

  // Pre-fill "posted wait" from ThemeParks.wiki live data (at most once per interaction)
  const livePrefillDone = useRef(false);
  const liveWait = useLiveWaitTime(entity);

  useEffect(() => {
    if (!interactionId) return;

    // Resilient: `LogActivityPicker` navigates here the moment the activity is
    // queued locally, before the server has it. See `resilientSnapshot.ts`.
    const unsub = onSnapshotResilient(
      doc(db, "interactions", interactionId),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as Interaction;
          setInteraction(data);
          setLoadedInteractionId(interactionId);
          // Each live editor follows the snapshot unless it has a save of its
          // own still waiting out the debounce — that's what's being typed, and
          // it goes up shortly. Seeding only once (as this used to) left every
          // phone holding the value it opened with, and each save then wrote
          // that stale copy over what everyone else had entered since. The edit
          // form is seeded separately, once, when it opens (see `openEdit`).
          const idle = (timer: TimerRef) => pendingWrites.current.get(timer)?.docId !== interactionId;
          const follows = (timer: TimerRef, path: string, value: unknown) =>
            idle(timer) && !isOwnEcho(sentWrites.current, timer, interactionId, path, value);
          const cr = data.customResponses as Record<string, unknown> | undefined;
          if (follows(notesSaveTimer, "notes", data.notes ?? null)) setLiveNotes(data.notes || "");
          if (follows(postedWaitSaveTimer, "postedWait", data.postedWait ?? null)) {
            setLivePostedWait(data.postedWait != null ? String(data.postedWait) : "");
          }
          if (follows(scheduledTimeSaveTimer, "customResponses.scheduled_time", cr?.scheduled_time ?? null)) {
            setLiveScheduledTime((cr?.scheduled_time as string) || "");
          }
          if (follows(scheduledBoardingSaveTimer, "customResponses.scheduled_boarding", cr?.scheduled_boarding ?? null)) {
            setLiveScheduledBoarding((cr?.scheduled_boarding as string) || "");
          }
          if (follows(locationSaveTimer, "customResponses.location", cr?.location ?? null)) {
            setLiveLocation((cr?.location as string) || "");
          }

          const crOrders = (cr?.orders as DiningOrder[]) || [];
          const crRounds = (cr?.rounds as LoungeRound[]) || [];
          if (!liveOrdersInitialized.current) {
            // Dining and lounges alike start in rounds mode — courses and drinks
            // arrive in waves, and each round carries its own placed/received
            // time. A linked entity's type lands later (it's fetched
            // separately), so an effect below re-applies the default once it does.
            setLiveOrders(crOrders);
            setLiveRounds(crRounds);
            setLiveUseRounds(defaultUsesRounds(getInteractionEntityType(null, cr), cr));
            liveOrdersInitialized.current = true;
          } else if (idle(ordersSaveTimer)) {
            setLiveOrders(crOrders);
            setLiveRounds(crRounds);
            // Follow a mode switch made elsewhere; with nothing logged either
            // way, keep the mode this screen is in.
            if (crRounds.length > 0) setLiveUseRounds(true);
            else if (crOrders.length > 0) setLiveUseRounds(false);
          }
          // "Who was here" writes immediately (no debounce), so it always follows.
          setLiveParticipants(Array.isArray(data.participants) ? data.participants : null);
          if (idle(accessTierSaveTimer)) setLiveAccessTier(data.accessTierId ?? null);
          if (idle(triviaSaveTimer)) {
            setLiveTriviaQuestions((cr?.questions as TriviaQuestion[]) || []);
            setLiveTriviaScore((cr?.score as string) || "");
            setLiveTriviaWinningScore((cr?.winningScore as string) || "");
          }
        }
        setLoading(false);
      },
      // This listener already swallowed the race quietly; retrying actually
      // recovers it instead of leaving a dead listener behind.
      {
        label: `interaction ${interactionId}`,
        onError: ({ error, hadPendingWrites }) => {
          setLoading(false);
          // Same as the trip page: don't keep rendering cached data the server
          // refuses, unless it's our own write still on its way up.
          if (error.code === "permission-denied" && !hadPendingWrites) setInteraction(null);
        },
      },
    );

    return unsub;
  }, [interactionId]);

  // Fetch entity
  useEffect(() => {
    const entityId = interaction?.entityId;
    if (!entityId) {
      setEntity(null);
      setLoadedEntityId(null);
      setEntityLoading(false);
      return;
    }
    // A slow read for the activity before must not land on top of this one's.
    let current = true;
    setEntityLoading(true);
    getDoc(doc(db, "entities", entityId)).then((snap) => {
      if (!current) return;
      setEntity(snap.exists() ? withCurrentType(snap.data() as Entity) : null);
      setLoadedEntityId(entityId);
    }).catch(() => {
      if (current) setEntity(null);
    }).finally(() => {
      if (current) setEntityLoading(false);
    });
    return () => {
      current = false;
    };
  }, [interaction?.entityId]);

  // Fetch trip for travel party
  useEffect(() => {
    if (!tripId) return;
    getDoc(doc(db, "trips", tripId)).then((snap) => {
      if (snap.exists()) setTrip(snap.data() as Trip);
    }).catch(() => setTrip(null));
  }, [tripId]);

  usePageBar({
    title: interaction ? interaction.name || entity?.displayName || "Activity" : null,
    parent: { to: `/trips/${tripId}`, label: trip?.name ?? "Trip" },
    cancel: editing ? { onClick: () => setEditing(false) } : null,
  });

  // Reset live-edit initialization when navigating to a different interaction
  useEffect(() => {
    liveOrdersInitialized.current = false;
    roundsDefaultApplied.current = false;
    livePrefillDone.current = false;
  }, [interactionId]);

  // Close the actions menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  // Pre-fill "posted wait" from the live wait time when the entity uses
  // posted_wait mode and nothing has been entered yet, and save it: the number
  // shown is the number recorded unless someone changes it. (It used to wait
  // for a tap on a small "live ✓" link, so a wait that looked recorded often
  // never reached the activity.) It only ever fills an empty wait, and a wait
  // typed on another phone still replaces it when that phone's save lands.
  // Nothing is filled until the activity and its place on hand are the ones
  // being shown (`liveWaitToPrefill`).
  useEffect(() => {
    if (livePrefillDone.current || !interactionId) return;
    const mins = liveWaitToPrefill({
      interactionId,
      loadedInteractionId,
      interaction,
      loadedEntityId,
      entityLoading,
      postedWaitMode: !!entity && resolveTimeInputMode(entity, entity.type, typeSettings) === "posted_wait",
      liveWaitLoading: liveWait.loading,
      liveWaitMinutes: liveWait.waitMinutes,
      inputText: livePostedWait,
    });
    if (mins == null) return;
    livePrefillDone.current = true;
    setLivePostedWait(String(mins));
    fireWrite(updateDoc(doc(db, "interactions", interactionId), {
      postedWait: mins,
      updatedAt: serverTimestamp(),
    }), "save live posted wait");
    setPostedWaitStatus("saved");
    if (postedWaitSavedStatusTimer.current) clearTimeout(postedWaitSavedStatusTimer.current);
    postedWaitSavedStatusTimer.current = setTimeout(() => setPostedWaitStatus("idle"), 1500);
  }, [interaction, interactionId, loadedInteractionId, entity, loadedEntityId, typeSettings, entityLoading, liveWait.loading, liveWait.waitMinutes, livePostedWait]);

  // A linked entity decides the activity's type, and it's fetched after the
  // first snapshot — so re-apply the rounds default once it lands. Only for an
  // activity with nothing logged yet; anything already recorded keeps its shape,
  // as does a mode the user picked.
  useEffect(() => {
    if (!interaction) return;
    // Wait for the entity rather than deciding off a half-loaded activity. (If
    // the fetch never lands, the snapshot-time default stands.)
    if (interaction.entityId && !entity) return;
    if (!liveOrdersInitialized.current || roundsDefaultApplied.current) return;
    roundsDefaultApplied.current = true;
    if (liveUseRounds || liveOrders.length > 0 || liveRounds.length > 0) return;
    const responses = interaction.customResponses as Record<string, unknown> | undefined;
    if (!defaultUsesRounds(getInteractionEntityType(entity, responses), responses)) return;
    setLiveUseRounds(true);
  }, [interaction, entity, liveUseRounds, liveOrders.length, liveRounds.length]);

  // Seed the live answers once both the activity and the place's questions are
  // known — the entity arrives in a later fetch, and its question list decides
  // which keys to read. Like the other live editors, each answer then follows
  // the snapshot except while its own save is pending (what's being typed).
  useEffect(() => {
    if (!interaction || !interactionId) return;
    if (interaction.entityId && !entity) return;
    const questions = resolveCustomQuestions(entity, typeSettings);
    setLiveAnswers((prev) => {
      const next = { ...prev };
      for (const q of questions) {
        const timer = answerTimers.current[q.id]?.save;
        if (timer && pendingWrites.current.get(timer)?.docId === interactionId) continue;
        const stored = (interaction.customResponses as Record<string, unknown> | undefined)?.[q.id] ?? null;
        if (timer && isOwnEcho(sentWrites.current, timer, interactionId, `customResponses.${q.id}`, stored)) continue;
        next[q.id] = customAnswer(interaction.customResponses, q);
      }
      return next;
    });
  }, [interaction, entity, typeSettings, interactionId]);

  // On unmount, send any save still waiting out its debounce — an edit made
  // just before tapping Back would otherwise be dropped with its timer — then
  // clear the timers.
  useEffect(() => {
    const timers = [
      notesSaveTimer, savedStatusTimer,
      postedWaitSaveTimer, postedWaitSavedStatusTimer,
      scheduledTimeSaveTimer, scheduledTimeSavedStatusTimer,
      scheduledBoardingSaveTimer, scheduledBoardingSavedStatusTimer,
      locationSaveTimer, locationSavedStatusTimer,
      ordersSaveTimer, ordersSavedStatusTimer,
      triviaSaveTimer, triviaSavedStatusTimer,
      participantsSavedStatusTimer,
      accessTierSaveTimer, accessTierSavedStatusTimer,
    ];
    const perQuestion = answerTimers.current;
    const pending = pendingWrites.current;
    return () => {
      for (const key of [...pending.keys()]) sendPendingWrite(pending, key);
      for (const t of timers) {
        if (t.current) clearTimeout(t.current);
      }
      for (const pair of Object.values(perQuestion)) {
        if (pair.save.current) clearTimeout(pair.save.current);
        if (pair.saved.current) clearTimeout(pair.saved.current);
      }
    };
  }, []);

  // ===== What's logged inside this, and what this is inside of =====
  // Derived above the early returns: `useEntityMap` is a hook, and hooks can't
  // sit behind a loading branch.
  const childActivities = tripInteractions.filter((i) => i.parentInteractionId === interactionId);
  // The whole subtree, not just the direct children: a lounge logged inside the
  // airport's restaurant should still show on the airport's page, nested where
  // it happened.
  const descendantIds = new Set(descendantActivityIds(tripInteractions, interactionId ?? ""));
  const descendantActivities = tripInteractions.filter((i) => descendantIds.has(i.id));
  const ancestorActivities = activityAncestors(tripInteractions, interactionId ?? "");
  const parentActivity = ancestorActivities[0] ?? null;
  // Candidates for "logged under" in the edit form: everything in the segment
  // this activity is being filed under. The activity's own subtree is excluded
  // by `buildNestedActivityOptions`, so it can't end up inside itself.
  const formSegmentId = editing ? editSegmentId : (interaction?.segmentId ?? null);
  const segmentActivities = tripInteractions.filter(
    (i) => (i.segmentId ?? null) === formSegmentId && i.id !== interactionId,
  );
  // A flight's airports too, so its title can read "Flight: BOS → MCO".
  const ownRoute = flightRoute(interaction?.customResponses as Record<string, unknown> | undefined);
  const relatedEntityIds = [
    ...new Set(
      [
        ...[...descendantActivities, ...ancestorActivities, ...segmentActivities].map((i) => i.entityId),
        ownRoute.from,
        ownRoute.to,
      ].filter((id): id is string => id != null),
    ),
  ];
  const { entityMap: relatedEntityMap, entityDataMap: relatedEntityDataMap } =
    useEntityMap(relatedEntityIds);
  const activityLabel = (a: Interaction & { id: string }): string =>
    a.name || (a.entityId && relatedEntityMap[a.entityId]) || "Unnamed activity";

  if (loading || entityLoading) return <div className="loading">Loading...</div>;
  if (!interaction || !interactionId) {
    return <p>Interaction not found. <Link to={`/trips/${tripId}`}>Back to trip</Link></p>;
  }

  const cr = interaction.customResponses as Record<string, unknown>;
  const travelParty: Record<string, TravelPartyMember> = trip?.travelParty || {};

  // Who was here: this activity's own list when it has one, otherwise the group
  // inherited from the place it was logged inside of, and then from its segment
  // — so a cruise day that says "Gran wasn't along", or a park check-in that
  // says who went in, holds for everything under it without re-picking.
  const partyIds = Object.keys(travelParty);
  const { participants: inheritedParticipants, sourceActivityId: participantsSourceActivityId } =
    inheritedActivityParticipants(
      { id: interactionId, segmentId: interaction.segmentId, parentInteractionId: interaction.parentInteractionId },
      tripInteractions,
      segmentParticipantsLookup(segments, partyIds),
      partyIds,
    );
  const presentParticipants =
    liveParticipants && liveParticipants.length > 0 ? liveParticipants : inheritedParticipants;
  const inheritsParticipants = !(liveParticipants && liveParticipants.length > 0);
  // Only credit the source when it actually narrowed the party — otherwise the
  // inherited group is just "everyone", whatever this sits under.
  const participantsSourceName =
    inheritedParticipants.length < partyIds.length
      ? participantsSourceActivityId
        ? ancestorActivities
            .filter((a) => a.id === participantsSourceActivityId)
            .map(activityLabel)[0] ?? null
        : segments.find((s) => s.id === interaction.segmentId)?.name ?? null
      : null;
  const hasOrders = ((cr?.orders as DiningOrder[]) || []).length > 0;
  const hasRounds = ((cr?.rounds as LoungeRound[]) || []).length > 0;
  // Timeline shown/measured with round placed/received times projected in
  // (read-only). Uses the live editor's rounds so it updates as you log a time,
  // before the debounced save round-trips.
  const timelineEvents = interactionTimelineEvents(interaction.events, liveUseRounds ? liveRounds : []);
  // Effective type drives event defaults and order tracking — a linked entity's
  // type, or the type chosen for an entity-less "custom" interaction.
  const effectiveType = getInteractionEntityType(entity, cr);
  // Read the place again after its button order is saved from here.
  const reloadEntity = () => {
    const id = interaction.entityId;
    if (!id) return;
    getDoc(doc(db, "entities", id))
      .then((snap) => {
        if (snap.exists()) setEntity(withCurrentType(snap.data() as Entity));
      })
      .catch(() => {});
  };
  const stepOptions = getEventOptionsForEntity(entity?.eventOptions, effectiveType, typeSettings?.eventOptions);
  const placeRoles = parseRoleList(entity?.customConfig?.staff_roles);
  const typeRoles = parseRoleList(typeSettings?.staffRoles);
  const roles = resolveActivityRoles(effectiveType, placeRoles, typeRoles);
  const showOrders = interactionUsesOrders(effectiveType, cr);
  // Trivia isn't only its own activity — a lounge or dining room hosts a round
  // of it — so any activity can opt in from the kebab menu (customResponses.hasTrivia).
  const isTrivia = interactionShowsTrivia(cr);
  // Nothing scored yet, so an opted-in module can still be taken back off.
  const triviaRecorded =
    liveTriviaQuestions.length > 0 || !!liveTriviaScore.trim() || !!liveTriviaWinningScore.trim();
  // The orders/rounds module is shown for order-ish activities or once rounds
  // exist; otherwise offer "Start rounds" from the kebab menu.
  const roundsModuleVisible = showOrders || hasOrders || hasRounds || liveUseRounds || liveRounds.length > 0;

  // Shared debounced auto-save for the live editors. Fires the write without
  // awaiting the server ack (which never resolves while offline) and optimistically
  // shows "Saved" once it's queued to the local cache — so offline edits don't
  // hang on a perpetual "Saving…".
  //
  // Payloads from edits inside one debounce window merge, later keys winning,
  // so an editor can send only the fields each edit touched and still have
  // them all go up together. The pending payload is what marks the editor as
  // mid-edit (see the snapshot listener) and what an unmount flushes.
  type SaveStatus = "idle" | "saving" | "saved";
  const saveDebounced = (
    saveTimer: TimerRef,
    savedTimer: TimerRef,
    setStatus: (s: SaveStatus) => void,
    payload: Record<string, unknown>,
    context: string,
  ) => {
    setStatus("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    const pending = pendingWrites.current;
    // Still holding a save for the activity this page showed before? Send it.
    if (pending.get(saveTimer)?.docId !== interactionId) sendPendingWrite(pending, saveTimer, sentWrites.current);
    pending.set(saveTimer, {
      docId: interactionId,
      payload: { ...pending.get(saveTimer)?.payload, ...payload },
      context,
    });
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      sendPendingWrite(pending, saveTimer, sentWrites.current);
      setStatus("saved");
      savedTimer.current = setTimeout(() => setStatus("idle"), 1500);
    }, 600);
  };

  const handleLiveNotesChange = (value: string) => {
    setLiveNotes(value);
    saveDebounced(notesSaveTimer, savedStatusTimer, setNotesStatus, {
      notes: value.trim() || null,
    }, "notes");
  };

  const handleLivePostedWaitChange = (value: string) => {
    setLivePostedWait(value);
    const trimmed = value.trim();
    const parsed = trimmed ? parseInt(trimmed, 10) : NaN;
    saveDebounced(postedWaitSaveTimer, postedWaitSavedStatusTimer, setPostedWaitStatus, {
      postedWait: Number.isFinite(parsed) ? parsed : null,
    }, "posted wait");
  };

  const handleLiveScheduledTimeChange = (value: string) => {
    setLiveScheduledTime(value);
    saveDebounced(scheduledTimeSaveTimer, scheduledTimeSavedStatusTimer, setScheduledTimeStatus, {
      "customResponses.scheduled_time": value || null,
    }, "scheduled time");
  };

  const handleLiveScheduledBoardingChange = (value: string) => {
    setLiveScheduledBoarding(value);
    saveDebounced(scheduledBoardingSaveTimer, scheduledBoardingSavedStatusTimer, setScheduledBoardingStatus, {
      "customResponses.scheduled_boarding": value || null,
    }, "boarding time");
  };

  const handleLiveOrdersChange = (data: { orders: DiningOrder[]; rounds: LoungeRound[]; useRounds: boolean }) => {
    const modeSwitched = data.useRounds !== liveUseRounds;
    setLiveOrders(data.orders);
    setLiveRounds(data.rounds);
    setLiveUseRounds(data.useRounds);
    // Update only the order/round keys so other customResponses fields
    // (activityType, entityType, trivia, scheduled_time) are preserved. Round
    // placed/received times live here; they're projected into the timeline at
    // read time (see interactionTimelineEvents), not persisted as extra events.
    //
    // And only the one being edited: an edit to the rounds says nothing about
    // the flat list, so writing it (as `[]`) could wipe orders someone else
    // entered in the meantime. Switching modes moves everything across, so
    // that edit writes both.
    saveDebounced(ordersSaveTimer, ordersSavedStatusTimer, setOrdersStatus, {
      ...(modeSwitched || !data.useRounds
        ? { "customResponses.orders": data.useRounds ? [] : data.orders }
        : {}),
      ...(modeSwitched || data.useRounds
        ? { "customResponses.rounds": data.useRounds ? data.rounds : [] }
        : {}),
    }, "orders/rounds");
  };

  // Start logging rounds on an activity that doesn't already surface the
  // orders/rounds module (e.g. a show or excursion) — seeds one empty round so
  // the editor appears.
  const handleStartRounds = () => {
    setMenuOpen(false);
    handleLiveOrdersChange({
      orders: [],
      rounds: liveRounds.length > 0 ? liveRounds : [{ orders: [] }],
      useRounds: true,
    });
  };

  const handleLiveAnswerChange = (question: CustomQuestion, value: string) => {
    setLiveAnswers((prev) => ({ ...prev, [question.id]: value }));
    const timers = (answerTimers.current[question.id] ??= {
      save: { current: null },
      saved: { current: null },
    });
    saveDebounced(
      timers.save,
      timers.saved,
      (st) => setAnswerStatus((prev) => ({ ...prev, [question.id]: st })),
      { [`customResponses.${question.id}`]: customAnswerValue(question, value) },
      question.label,
    );
  };

  const handleLiveLocationChange = (value: string) => {
    setLiveLocation(value);
    saveDebounced(locationSaveTimer, locationSavedStatusTimer, setLocationStatus, {
      "customResponses.location": value.trim() || null,
    }, "location");
  };

  // Add the trivia module to an activity that isn't a trivia activity — the
  // lounge you played a round in. The flag is what keeps it on screen across
  // reloads until something is actually scored; clearing it takes an
  // added-by-mistake module back off again.
  const setHasTrivia = (on: boolean) => {
    setMenuOpen(false);
    fireWrite(updateDoc(doc(db, "interactions", interactionId), {
      "customResponses.hasTrivia": on,
      updatedAt: serverTimestamp(),
    }), on ? "add trivia" : "remove trivia");
    setInteraction((prev) =>
      prev
        ? { ...prev, customResponses: { ...(prev.customResponses as Record<string, unknown>), hasTrivia: on } }
        : prev,
    );
  };

  // A flight's two airports. Each side saves on its own the moment it's
  // picked, like the trivia flag: there's nothing to type, so nothing to
  // debounce.
  const setFlightAirport = (key: typeof FROM_AIRPORT_KEY | typeof TO_AIRPORT_KEY, airportId: string | null) => {
    fireWrite(updateDoc(doc(db, "interactions", interactionId), {
      [`customResponses.${key}`]: airportId,
      updatedAt: serverTimestamp(),
    }), "flight airport");
    setInteraction((prev) =>
      prev
        ? { ...prev, customResponses: { ...(prev.customResponses as Record<string, unknown>), [key]: airportId } }
        : prev,
    );
  };

  const handleLiveTriviaChange = (data: { questions: TriviaQuestion[]; score: string; winningScore: string }) => {
    // Only the parts this edit touched — typing the score shouldn't rewrite the
    // question list from this screen's copy of it.
    const payload: Record<string, unknown> = {};
    if (!deepEqual(data.questions, liveTriviaQuestions)) payload["customResponses.questions"] = data.questions;
    if (data.score !== liveTriviaScore) payload["customResponses.score"] = data.score;
    if (data.winningScore !== liveTriviaWinningScore) payload["customResponses.winningScore"] = data.winningScore;
    setLiveTriviaQuestions(data.questions);
    setLiveTriviaScore(data.score);
    setLiveTriviaWinningScore(data.winningScore);
    if (Object.keys(payload).length === 0) return;
    saveDebounced(triviaSaveTimer, triviaSavedStatusTimer, setTriviaStatus, payload, "trivia");
  };

  const handleSelectAccessTier = (optionId: string) => {
    // Tap the selected option again to clear it.
    const next = liveAccessTier === optionId ? null : optionId;
    setLiveAccessTier(next);
    saveDebounced(accessTierSaveTimer, accessTierSavedStatusTimer, setAccessTierStatus, {
      accessTierId: next,
    }, "access tier");
  };

  const handleToggleParticipant = (memberId: string) => {
    // The first toggle starts from whatever is showing — the inherited group —
    // and adds or removes the tapped member, which makes this activity's list
    // explicit from then on.
    const current = presentParticipants;
    const removing = current.includes(memberId);
    const next = removing ? current.filter((id) => id !== memberId) : [...current, memberId];
    setLiveParticipants(next);
    // Once the list is explicit, a tap adds or removes just that member, so two
    // people ticking different names at once both land. Only the first tap,
    // which turns the inherited group into this activity's own, writes a whole
    // list. Written straight away rather than debounced: a transform per tap
    // can't be merged with the next tap's (one transform per field per write).
    const value = inheritsParticipants ? next : removing ? arrayRemove(memberId) : arrayUnion(memberId);
    fireWrite(updateDoc(doc(db, "interactions", interactionId), {
      participants: value,
      updatedAt: serverTimestamp(),
    }), "who-was-here");
    if (participantsSavedStatusTimer.current) clearTimeout(participantsSavedStatusTimer.current);
    setParticipantsStatus("saved");
    participantsSavedStatusTimer.current = setTimeout(() => setParticipantsStatus("idle"), 1500);
  };

  // Staff & crew, saved as they're changed. An add or a remove touches just
  // that person, so two phones adding at once both land; an edit rewrites the
  // list, which is all a changed entry can be.
  const handleAddPerson = (person: Person) => {
    setInteraction((prev) => (prev ? { ...prev, people: [...(prev.people ?? []), person] } : prev));
    fireWrite(updateDoc(doc(db, "interactions", interactionId), {
      people: arrayUnion(person),
      updatedAt: serverTimestamp(),
    }), "add person");
  };

  const handleUpdatePerson = (index: number, person: Person) => {
    const next = (interaction?.people ?? []).map((p, i) => (i === index ? person : p));
    setInteraction((prev) => (prev ? { ...prev, people: next } : prev));
    fireWrite(updateDoc(doc(db, "interactions", interactionId), {
      people: next,
      updatedAt: serverTimestamp(),
    }), "edit person");
  };

  const handleRemovePerson = (index: number) => {
    const target = interaction?.people?.[index];
    if (!target) return;
    setInteraction((prev) =>
      prev ? { ...prev, people: (prev.people ?? []).filter((_, i) => i !== index) } : prev,
    );
    fireWrite(updateDoc(doc(db, "interactions", interactionId), {
      people: arrayRemove(target),
      updatedAt: serverTimestamp(),
    }), "remove person");
  };

  // Which roles this kind of activity offers up front — for every activity of
  // its type, or just this place. Null puts that scope back to its default.
  const handleSaveRoleList = (scope: RoleListScope, list: RoleTypeOption[] | null) => {
    if (scope === "place") {
      const entityId = interaction?.entityId;
      if (!entityId) return;
      setEntity((prev) =>
        prev ? { ...prev, customConfig: { ...(prev.customConfig ?? {}), staff_roles: list ?? undefined } } : prev,
      );
      fireWrite(updateDoc(doc(db, "entities", entityId), {
        "customConfig.staff_roles": list ?? deleteField(),
        updatedAt: serverTimestamp(),
      }), "save place roles");
      return;
    }
    const type = getInteractionEntityType(entity, interaction?.customResponses as Record<string, unknown> | undefined);
    if (!type) return;
    fireWrite(setDoc(doc(db, "typeSettings", type), {
      staffRoles: list ?? deleteField(),
      updatedAt: serverTimestamp(),
    }, { merge: true }), "save type roles");
  };

  const logEvent = (event: { eventType: EventType; notes: string; customLabel?: string }): InteractionEvent => {
    const newEvent: InteractionEvent = {
      eventType: event.eventType,
      eventTimestamp: Timestamp.now(),
      notes: event.notes || null,
      customLabel: event.customLabel || null,
      // The clock it was logged on, so it reads right wherever it's viewed.
      timezone: deviceZone() ?? null,
    };
    // Reflect the new event locally right away — the timeline renders straight
    // off `interaction.events`, and Firestore coalesces the first write into a
    // freshly-attached listener's initial sync, so we can't rely on the snapshot
    // to surface it immediately (that's the "doesn't show till refresh" bug).
    setInteraction((prev) => (prev ? { ...prev, events: [...(prev.events ?? []), newEvent] } : prev));
    fireWrite(updateDoc(doc(db, "interactions", interactionId), {
      events: arrayUnion(newEvent),
      updatedAt: serverTimestamp(),
    }), "add event");
    return newEvent;
  };
  const handleAddEvent = async (event: { eventType: EventType; notes: string; customLabel?: string }) => {
    logEvent(event);
  };

  // Events are only ever added and removed individually (arrayUnion /
  // arrayRemove), never by writing the array back: a watch appends events on
  // its own, often offline for hours, and a whole-array write from this
  // screen's copy would erase whatever it added meanwhile. arrayRemove matches
  // by deep equality, so the event passed is exactly the one the snapshot
  // delivered, Timestamps and all. Nothing relies on stored order — the
  // timeline sorts by eventTimestamp — so an edited event moving to the end of
  // the array doesn't matter.
  //
  // The timeline hands over the event itself, never a position (positions
  // shift as events are added, edited and removed elsewhere). Round-derived
  // rows (`roundIndex` set) are projections of a round's times, not stored
  // events, so there is nothing to remove or edit. The event must still be in
  // the stored list: if another device already changed or removed it, editing
  // the stale copy would bring it back.
  const storedEvent = (event: InteractionEvent): InteractionEvent | null => {
    if (event.roundIndex != null) return null;
    return (interaction?.events ?? []).find((e) => deepEqual(e, event)) ?? null;
  };

  const handleRemoveEvent = async (event: InteractionEvent) => {
    const target = storedEvent(event);
    if (!target) return;
    setInteraction((prev) =>
      prev ? { ...prev, events: (prev.events ?? []).filter((e) => !deepEqual(e, target)) } : prev,
    );
    fireWrite(updateDoc(doc(db, "interactions", interactionId), {
      events: arrayRemove(target),
      updatedAt: serverTimestamp(),
    }), "remove event");
  };

  const handleUpdateEvent = async (
    event: InteractionEvent,
    patch: { eventTimestamp?: Timestamp; notes?: string | null; customLabel?: string | null; timezone?: string | null },
  ) => {
    const target = storedEvent(event);
    if (!target) {
      if (event.roundIndex == null) {
        alert("That step was changed on another device while you were editing it. Take a look and edit it again.");
      }
      return;
    }
    // Spread the original so every other field (its timezone, anything a watch
    // wrote) survives the edit.
    const updated = withoutUndefined({ ...target, ...patch });
    setInteraction((prev) =>
      prev
        ? { ...prev, events: (prev.events ?? []).map((e) => (deepEqual(e, target) ? updated : e)) }
        : prev,
    );
    // Swap the one event: remove the original, add the edit. Firestore allows
    // only one transform per field per update, so it's two updates of the same
    // doc in one batch — atomic, and unlike a transaction it works offline.
    const ref = doc(db, "interactions", interactionId);
    const batch = writeBatch(db);
    batch.update(ref, { events: arrayRemove(target) });
    batch.update(ref, { events: arrayUnion(updated), updatedAt: serverTimestamp() });
    fireWrite(batch.commit(), "update event");
  };

  const timeInputMode = resolveTimeInputMode(entity, effectiveType, typeSettings);
  const customQuestions = resolveCustomQuestions(entity, typeSettings);

  // Seed the edit form once, as it opens. (It used to be re-seeded from every
  // snapshot, which wiped whatever was being typed whenever anyone else — or
  // the watch — touched the activity.) What it opened with is kept, so a save
  // writes only the fields changed here.
  const openEdit = () => {
    const et = normalizeEntityType(cr?.entityType);
    const opened: EditFormValues = {
      name: interaction.name || "",
      segmentId: interaction.segmentId || null,
      parentId: interaction.parentInteractionId || null,
      entityType:
        et && CUSTOM_INTERACTION_TYPES.includes(et) ? et : "",
      orders: (cr?.orders as DiningOrder[]) || [],
      rounds: (cr?.rounds as LoungeRound[]) || [],
      useRounds: liveUseRounds,
      // Trivia and the scheduled time come from the live editors, which already
      // hold anything typed there that's still on its way up.
      triviaQuestions: liveTriviaQuestions,
      triviaScore: liveTriviaScore,
      triviaWinningScore: liveTriviaWinningScore,
      postedWait: interaction.postedWait != null ? String(interaction.postedWait) : "",
      scheduledTime: liveScheduledTime,
      scheduledBoarding: liveScheduledBoarding,
    };
    editOpened.current = opened;
    setEditName(opened.name);
    setEditSegmentId(opened.segmentId);
    setEditParentId(opened.parentId);
    setEditEntityType(opened.entityType);
    setEditOrders(opened.orders);
    setEditRounds(opened.rounds);
    setEditUseRounds(opened.useRounds);
    setEditTriviaQuestions(opened.triviaQuestions);
    setEditTriviaScore(opened.triviaScore);
    setEditTriviaWinningScore(opened.triviaWinningScore);
    setEditPostedWait(opened.postedWait);
    setEditScheduledTime(opened.scheduledTime);
    setEditScheduledBoarding(opened.scheduledBoarding);
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    const opened = editOpened.current;
    if (!opened) return;
    // Only what changed since the form opened, each at its own path. Writing
    // the whole customResponses map (or `people`) from the form's copy reverted
    // everything anyone else saved while it was open: orders, answers to the
    // place's questions, the location, the scheduled time.
    const updates: Record<string, unknown> = {};
    // customResponses keys to set; `undefined` deletes the key.
    const crUpdates: Record<string, unknown> = {};

    if (editName !== opened.name) updates.name = editName.trim() || null;
    // Only shown for posted_wait entities, so it only changes for one.
    if (editPostedWait !== opened.postedWait) {
      updates.postedWait = editPostedWait ? parseInt(editPostedWait, 10) || null : null;
    }
    // Custom type — only meaningful for entity-less interactions. Editable via
    // the Type field below; "" clears it back to untyped.
    if (!interaction.entityId && editEntityType !== opened.entityType) {
      crUpdates.entityType = editEntityType || undefined;
    }
    if (editScheduledTime !== opened.scheduledTime) {
      crUpdates.scheduled_time = editScheduledTime || null;
    }
    if (editScheduledBoarding !== opened.scheduledBoarding) {
      crUpdates.scheduled_boarding = editScheduledBoarding || null;
    }

    // Orders (unified)
    if (
      editUseRounds !== opened.useRounds ||
      !deepEqual(editOrders, opened.orders) ||
      !deepEqual(editRounds, opened.rounds)
    ) {
      if (editUseRounds) {
        crUpdates.rounds = editRounds
          .map((r) => ({
            orders: r.orders.filter((o) => o.order.trim()),
            // Preserve per-round timing logged via the inline editor.
            ...(r.placedAt ? { placedAt: r.placedAt } : {}),
            ...(r.receivedAt ? { receivedAt: r.receivedAt } : {}),
          }))
          // A round with only its times logged is still a round.
          .filter((r) => r.orders.length > 0 || r.placedAt || r.receivedAt);
        // Clear flat orders when using rounds
        crUpdates.orders = [];
      } else {
        crUpdates.orders = editOrders.filter((o) => o.order.trim());
        // Clear rounds when using flat orders
        crUpdates.rounds = [];
      }
    }

    // Trivia
    if (!deepEqual(editTriviaQuestions, opened.triviaQuestions)) {
      crUpdates.questions = editTriviaQuestions.filter((q) => q.question.trim() || q.answer.trim());
    }
    if (editTriviaScore !== opened.triviaScore) crUpdates.score = editTriviaScore;
    if (editTriviaWinningScore !== opened.triviaWinningScore) {
      crUpdates.winningScore = editTriviaWinningScore;
    }

    const payload: Record<string, unknown> = { ...updates };
    for (const [key, value] of Object.entries(crUpdates)) {
      payload[`customResponses.${key}`] = value === undefined ? deleteField() : value;
    }
    if (Object.keys(payload).length > 0) {
      // Reflect the edit locally right away (e.g. renaming a dining activity to
      // "Breakfast") — the detail view renders off the snapshot, whose first
      // write Firestore folds into the listener's initial sync, so it otherwise
      // wouldn't show till refresh.
      setInteraction((prev) => {
        if (!prev) return prev;
        const nextCr = { ...(prev.customResponses as Record<string, unknown>) };
        for (const [key, value] of Object.entries(crUpdates)) {
          if (value === undefined) delete nextCr[key];
          else nextCr[key] = value;
        }
        return { ...prev, ...updates, customResponses: nextCr } as Interaction;
      });
      // Fire the write but close the form immediately — offline the ack never
      // comes, yet the change is already in the local cache (see offlineWrites).
      fireWrite(
        updateDoc(doc(db, "interactions", interactionId), { ...payload, updatedAt: serverTimestamp() }),
        "save interaction",
      );
    }

    // Where it's filed is a move, not a field edit: anything logged inside this
    // has to come with it, or half the tree ends up in a segment its parent
    // left. `moveActivity` writes the whole subtree in one batch. Measured
    // against what the form opened with, so an untouched field can't move it
    // back from wherever someone else has filed it since.
    const nextSegmentId = editSegmentId ?? null;
    const nextParentId = editParentId ?? null;
    if (opened.segmentId !== nextSegmentId || opened.parentId !== nextParentId) {
      moveActivity(
        interactionId,
        { segmentId: nextSegmentId, parentInteractionId: nextParentId },
        tripInteractions,
      );
      setInteraction((prev) =>
        prev ? { ...prev, segmentId: nextSegmentId, parentInteractionId: nextParentId } : prev,
      );
    }
    editOpened.current = null;
    setEditing(false);
  };

  const handleDelete = async () => {
    // Deleting the park check-in mustn't take the day's rides with it — they
    // were really logged. They move up to wherever this sat instead.
    const message =
      childActivities.length > 0
        ? `Delete this interaction? This cannot be undone. The ${childActivities.length} ` +
          `activit${childActivities.length === 1 ? "y" : "ies"} logged inside it will be kept, ` +
          `moved up to ${parentActivity ? activityLabel(parentActivity) : "the segment"}.`
        : "Delete this interaction? This cannot be undone.";
    if (!confirm(message)) return;
    setDeleting(true);
    deleteActivityLiftingChildren(interactionId, tripInteractions);
    navigate(`/trips/${tripId}`);
  };

  // When the activity happened — its own first event, else the day its segment
  // covers. The time is only shown when the activity actually carries one;
  // a backfilled note often records the place and the people but no clock.
  const whenDate = activityStart(interaction, segmentDayLookup(segments));
  const ownStart = getActivityTimeRange(interaction.events).start;
  // The clock this activity ran on — where its first step was logged, else
  // its segment's, or the trip's.
  const zone = activityZone(interaction, segmentZoneLookup(segments, trip?.timezone ?? undefined));

  // Check if there's any custom data to show the edit sections
  const showTriviaInEdit = isTrivia || editTriviaQuestions.length > 0;

  return (
    <div className="page">
      {editing ? (
        <div className="edit-section">
          <label className="form-field">
            <span className="form-label">Name</span>
            <input
              type="text"
              className="form-input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
          </label>
          {segments.length > 0 && (
            <label className="form-field">
              <span className="form-label">Segment</span>
              <select
                className="form-input"
                value={editSegmentId || ""}
                onChange={(e) => {
                  const next = e.target.value || null;
                  setEditSegmentId(next);
                  // A place in the old segment can't hold this any more.
                  setEditParentId((prev) =>
                    prev && tripInteractions.some((i) => i.id === prev && (i.segmentId ?? null) === next)
                      ? prev
                      : null,
                  );
                }}
              >
                <option value="">No segment</option>
                {buildNestedSegmentOptions(segments).map(({ id, label }) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </label>
          )}

          {/* What this was logged inside of — the park check-in that holds it,
              the airport it happened in. Anything logged inside *this* comes
              along if it moves. */}
          {segmentActivities.length > 0 && (
            <label className="form-field">
              <span className="form-label">Logged inside</span>
              <select
                className="form-input"
                value={editParentId || ""}
                onChange={(e) => setEditParentId(e.target.value || null)}
              >
                <option value="">Not inside anything</option>
                {buildNestedActivityOptions(segmentActivities, activityLabel, {
                  excludeSubtreeOf: interactionId,
                }).map(({ id, label }) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </label>
          )}

          {/* Type — only for entity-less ("custom") activities. A linked entity
              already defines the type, so the field is hidden in that case. */}
          {!interaction.entityId && (
            <label className="form-field">
              <span className="form-label">Type</span>
              <select
                className="form-input"
                value={editEntityType}
                onChange={(e) => setEditEntityType(e.target.value as EntityType | "")}
              >
                <option value="">No type</option>
                {CUSTOM_INTERACTION_TYPES.map((t) => (
                  <option key={t} value={t}>{entityTypeLabel(t)}</option>
                ))}
              </select>
            </label>
          )}

          {/* Posted Wait / Scheduled Time */}
          {timeInputMode === "posted_wait" && (
            <label className="form-field">
              <span className="form-label">Posted Wait (minutes)</span>
              <input
                type="number"
                className="form-input"
                min="0"
                value={editPostedWait}
                onChange={(e) => setEditPostedWait(e.target.value)}
                placeholder="e.g. 45"
              />
            </label>
          )}
          {(timeInputMode === "scheduled_time" || timeInputMode === "flight_times") && (
            <label className="form-field">
              <span className="form-label">{timeInputMode === "flight_times" ? "Departs" : "Scheduled Time"}</span>
              <input
                type="time"
                className="form-input"
                value={editScheduledTime}
                onChange={(e) => setEditScheduledTime(e.target.value)}
              />
            </label>
          )}
          {timeInputMode === "flight_times" && (
            <label className="form-field">
              <span className="form-label">Boarding</span>
              <input
                type="time"
                className="form-input"
                value={editScheduledBoarding}
                onChange={(e) => setEditScheduledBoarding(e.target.value)}
              />
            </label>
          )}

          {/* Staff & crew are edited in place on the detail view (below). */}

          {/* Orders are edited inline on the detail view (below), not here. */}

          {/* Trivia editor */}
          {showTriviaInEdit && (
            <TriviaEditor
              questions={editTriviaQuestions}
              score={editTriviaScore}
              winningScore={editTriviaWinningScore}
              onChange={(data) => {
                setEditTriviaQuestions(data.questions);
                setEditTriviaScore(data.score);
                setEditTriviaWinningScore(data.winningScore);
              }}
            />
          )}

          <div className="form-actions">
            <button className="btn btn-small" onClick={handleSaveEdit}>Save</button>
            <button className="btn btn-secondary btn-small" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          {/* ===== Spine header: status card (the one shared header) ===== */}
          {(() => {
            // The wait itself is on the card as "Waited", next to the posted one.
            const totalDelay = calcTotalDelayTime(timelineEvents);
            // With rounds, the card's rounds chip gives the average across all
            // of them; the first order alone would read as a different answer.
            const orderToReceived = timelineEvents.some((e) => e.roundIndex != null)
              ? 0
              : calcOrderToReceivedTime(timelineEvents);
            // Universal Express etc.: expected wait ≈ 50% of the posted standby.
            const selectedTierName =
              entity?.activityOptions?.find((o) => o.id === liveAccessTier)?.displayName ?? null;
            const postedForExpected = parseInt(livePostedWait, 10);
            const expectedWait = expectedExpressWait(
              Number.isFinite(postedForExpected) ? postedForExpected : null,
              selectedTierName,
            );
            // Against the schedule: a show's start, a flight's boarding and
            // departure (see utils/scheduledTimes).
            const show = timeInputMode === "scheduled_time"
              ? showTiming(interaction.events, liveScheduledTime, zone)
              : null;
            const flight = timeInputMode === "flight_times"
              ? flightTiming(interaction.events, liveScheduledTime, liveScheduledBoarding, zone)
              : null;
            const showtimeOptions =
              timeInputMode === "scheduled_time" && !liveScheduledTime && liveWait.showtimes.length > 0
                ? showtimeChoices(
                    liveWait.showtimes,
                    getActivityTimeRange(interaction.events).start ?? new Date(),
                    zone,
                  ).slice(0, 4)
                : [];
            const clockLabel = (hhmm: string) => {
              const [h, m] = hhmm.split(":").map(Number);
              return new Date(2000, 0, 1, h, m).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
            };
            const timingCell = (key: string, label: string, seconds: number | null, title: string) =>
              seconds == null ? null : (
                <div className={`p${seconds >= 60 ? " p-delay" : ""}`} key={key} title={title}>
                  <div className="k">{label}</div>
                  <div className="v">{formatLateness(seconds, formatDuration)}</div>
                </div>
              );
            const eventCount = interaction.events?.length ?? 0;
            const active = isActivityActive(
              interaction.events,
              getInteractionEntityType(entity, interaction.customResponses as Record<string, unknown> | undefined),
              interaction.customResponses as Record<string, unknown> | undefined,
            );
            const status: ActivityStatus = eventCount === 0 ? "ready" : active ? "live" : "wrapped";
            const seg = interaction.segmentId
              ? segments.find((s) => s.id === interaction.segmentId)
              : null;
            const title = titleWithRoute(
              interaction.name || entity?.displayName || "Unnamed Activity",
              flightRouteLabel(ownRoute, (id) => relatedEntityMap[id]),
            );
            const meta: ReactNode[] = [
              // Renamed ("LAX", "UA 123"): keep the place it was logged at in view.
              interaction.name && entity?.displayName && interaction.name !== entity.displayName
                ? entity.displayName
                : null,
              parentActivity ? (
                <span className="dp-inside">
                  inside{" "}
                  <Link to={`/trips/${tripId}/interactions/${parentActivity.id}`}>
                    {activityLabel(parentActivity)}
                  </Link>
                </span>
              ) : null,
              effectiveType ? entityTypeLabel(effectiveType) : null,
              // The way to the trip and the day it's on, wherever you came from
              // (the top bar's back may go to Link activities or Search instead).
              trip?.name ? <Link to={`/trips/${tripId}`}>{trip.name}</Link> : null,
              seg ? <Link to={`/trips/${tripId}/segments/${seg.id}`}>{seg.name}</Link> : null,
              whenDate
                ? `${whenDate.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: zone })}${
                    ownStart
                      ? ` · ${ownStart.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: zone })}`
                      : ""
                  }${ownStart && zoneNote(ownStart, zone) ? ` ${zoneNote(ownStart, zone)}` : ""}`
                : null,
            ];
            return (
              <ActivityHero
                name={title}
                icon={
                  entity?.imageUrl ? (
                    <img src={entity.imageUrl} alt="" />
                  ) : (
                    <TypeIcon type={effectiveType} name={entity?.displayName} tile />
                  )
                }
                meta={meta}
                status={status}
                events={timelineEvents}
                options={stepOptions}
                zone={zone}
                postedWait={parseInt(livePostedWait, 10) || null}
                onLog={(opt) => logEvent({ eventType: opt.eventType, notes: "", customLabel: opt.displayName })}
                onUndo={(e) => void handleRemoveEvent(e)}
                menu={
                  <div className="dp-actions" ref={menuRef}>
                  <button
                    className="dp-menu-trigger"
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label="Actions"
                  >
                    &#8942;
                  </button>
                  {menuOpen && (
                    <div className="detail-menu-dropdown">
                      {interaction.entityId && (
                        // The place itself, with every other visit to it —
                        // the only way from a logged activity to its entity.
                        <Link to={`/entities/${interaction.entityId}`} onClick={() => setMenuOpen(false)}>
                          View entity
                        </Link>
                      )}
                      <button onClick={() => { openEdit(); setMenuOpen(false); }}>Edit</button>
                      <button onClick={() => { setMenuOpen(false); setAddPersonSignal((n) => n + 1); }}>
                        Add person
                      </button>
                      {interaction.entityId ? (
                        <button onClick={() => { setMenuOpen(false); unlinkInteraction(interactionId); }}>
                          Unlink from place
                        </button>
                      ) : (
                        <>
                          <button onClick={() => { setLinkingPlace(true); setMenuOpen(false); }}>
                            Link to a place
                          </button>
                          {/* Breakfast, the safety drill, the flight home —
                              saying so is an answer, not a deferral. */}
                          <button
                            onClick={() => {
                              setMenuOpen(false);
                              setLinkingPlace(false);
                              setActivitiesNoPlace([interactionId], !interaction.noPlace);
                            }}
                          >
                            {interaction.noPlace ? "Needs a place after all" : "Not a place"}
                          </button>
                        </>
                      )}
                      {!roundsModuleVisible && (
                        <button onClick={handleStartRounds}>Start rounds</button>
                      )}
                      {!isTrivia && (
                        <button onClick={() => setHasTrivia(true)}>Add trivia</button>
                      )}
                      {cr?.hasTrivia === true && !triviaRecorded && (
                        <button onClick={() => setHasTrivia(false)}>Remove trivia</button>
                      )}
                      <button
                        className="detail-menu-danger"
                        onClick={() => { setMenuOpen(false); handleDelete(); }}
                        disabled={deleting}
                      >
                        {deleting ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  )}
                </div>
                }
                extra={
                  <>
                  {!interaction.entityId && !interaction.noPlace && !linkingPlace && (
                    <button
                      type="button"
                      className="dp-linkplace"
                      onClick={() => setLinkingPlace(true)}
                    >
                      No place linked — add one
                    </button>
                  )}
                  </>
                }
                editButtons={
                  <StepOrderButton
                    entityId={interaction.entityId}
                    entityName={entity?.displayName ?? null}
                    type={normalizeEntityType(effectiveType) ?? undefined}
                    options={stepOptions}
                    events={timelineEvents}
                    className="ln-edit"
                    onSaved={reloadEntity}
                  />
                }
                stats={
                (timeInputMode === "posted_wait" ||
                  timeInputMode === "scheduled_time" ||
                  timeInputMode === "flight_times" ||
                  expectedWait != null ||
                  orderToReceived > 0 ||
                  totalDelay > 0) && (
                  <div className="dp-perf">
                    {timeInputMode === "posted_wait" && (
                      <div className="p">
                        <div className="k">Posted wait</div>
                        <div className="v">
                          <input
                            type="number"
                            inputMode="numeric"
                            min="0"
                            placeholder="—"
                            value={livePostedWait}
                            onChange={(e) => handleLivePostedWaitChange(e.target.value)}
                            className="anchor-input"
                          />
                          <span className="dp-unit">m</span>
                          {postedWaitStatus === "saving" && <span className="dp-save">·</span>}
                          {postedWaitStatus === "saved" && <span className="dp-save dp-save-on">✓</span>}
                        </div>
                      </div>
                    )}
                    {expectedWait != null && (
                      <div className="p" title="Expected express wait — about half the posted standby">
                        <div className="k">Expected</div>
                        <div className="v">
                          {expectedWait}
                          <span className="dp-unit">m</span>
                        </div>
                      </div>
                    )}
                    {(timeInputMode === "scheduled_time" || timeInputMode === "flight_times") && (
                      <div className="p">
                        <div className="k">{timeInputMode === "flight_times" ? "Departs" : "Showtime"}</div>
                        <div className="v">
                          <input
                            type="time"
                            value={liveScheduledTime}
                            onChange={(e) => handleLiveScheduledTimeChange(e.target.value)}
                            className="anchor-input anchor-input-time"
                            aria-label={timeInputMode === "flight_times" ? "Scheduled departure" : "Scheduled time"}
                          />
                          {scheduledTimeStatus === "saving" && <span className="dp-save">·</span>}
                          {scheduledTimeStatus === "saved" && <span className="dp-save dp-save-on">✓</span>}
                        </div>
                        {showtimeOptions.length > 0 && (
                          <div className="dp-showtimes">
                            {showtimeOptions.map((t) => (
                              <button
                                key={t}
                                type="button"
                                className="dp-showtime"
                                onClick={() => handleLiveScheduledTimeChange(t)}
                              >
                                {clockLabel(t)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {timeInputMode === "flight_times" && (
                      <div className="p">
                        <div className="k">Boarding</div>
                        <div className="v">
                          <input
                            type="time"
                            value={liveScheduledBoarding}
                            onChange={(e) => handleLiveScheduledBoardingChange(e.target.value)}
                            className="anchor-input anchor-input-time"
                            aria-label="Scheduled boarding"
                          />
                          {scheduledBoardingStatus === "saving" && <span className="dp-save">·</span>}
                          {scheduledBoardingStatus === "saved" && <span className="dp-save dp-save-on">✓</span>}
                        </div>
                      </div>
                    )}
                    {show?.earlyBy != null && show.earlyBy > 0 && (
                      <div className="p" title="From the first step you logged to the showtime">
                        <div className="k">Early by</div>
                        <div className="v">{formatDuration(show.earlyBy)}</div>
                      </div>
                    )}
                    {show && timingCell("started", "Started", show.startedLate, "Show Started against the showtime")}
                    {flight?.earlyBy != null && flight.earlyBy > 0 && (
                      <div className="p" title="From the first step you logged to boarding time">
                        <div className="k">Early by</div>
                        <div className="v">{formatDuration(flight.earlyBy)}</div>
                      </div>
                    )}
                    {flight && timingCell("boarded", "Boarded", flight.boardingLate, "Boarding against the boarding time")}
                    {flight && timingCell("departed", "Departed", flight.departedLate, "Departed against the departure time")}
                    {orderToReceived > 0 && (
                      <div className="p">
                        <div className="k">Order → got</div>
                        <div className="v">{formatDuration(orderToReceived)}</div>
                      </div>
                    )}
                    {totalDelay > 0 && (
                      <div className="p p-delay">
                        <div className="k">Delay</div>
                        <div className="v">{formatDuration(totalDelay)}</div>
                      </div>
                    )}
                  </div>
                )
                }
              />
            );
          })()}

          {/* Where a flight flew between: two Airport places, so the airports
              collect their flights and the travel day can say "BOS → MCO". */}
          {timeInputMode === "flight_times" && (() => {
            const route = flightRoute(cr);
            return (
              <div className="flight-route" aria-label="Route">
                <div className="form-field">
                  <span className="form-label">From</span>
                  <EntityPicker
                    types={["AIRPORT"]}
                    value={route.from}
                    onChange={(id) => setFlightAirport(FROM_AIRPORT_KEY, id)}
                    placeholder="Departure airport"
                    emptyHint="No airports yet. Add one under Places."
                  />
                </div>
                <span className="flight-route-arrow" aria-hidden="true">→</span>
                <div className="form-field">
                  <span className="form-label">To</span>
                  <EntityPicker
                    types={["AIRPORT"]}
                    value={route.to}
                    onChange={(id) => setFlightAirport(TO_AIRPORT_KEY, id)}
                    placeholder="Arrival airport"
                    emptyHint="No airports yet. Add one under Places."
                  />
                </div>
              </div>
            );
          })()}

          {linkingPlace && !interaction.entityId && (
            <LinkPlacePanel
              interactionId={interactionId}
              tripId={tripId!}
              name={interaction.name}
              customResponses={interaction.customResponses}
              onClose={() => setLinkingPlace(false)}
            />
          )}

          {/* ===== Access tier (the place's activityOptions: Lightning Lane vs
                   Standby, Quick Service vs Sit-down, etc.) ===== */}
          {entity?.activityOptions && entity.activityOptions.length > 0 && (() => {
            const opts = entity.activityOptions;
            const types = [...new Set(opts.map((o) => o.optionType).filter(Boolean))];
            const label =
              types.length === 1
                ? types[0]!.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                : "Access";
            return (
              <div className="who-here">
                <div className="who-here-head">
                  <span className="who-here-label">{label}</span>
                  {accessTierStatus === "saving" && <span className="live-notes-status">Saving…</span>}
                  {accessTierStatus === "saved" && (
                    <span className="live-notes-status live-notes-status-saved">Saved</span>
                  )}
                </div>
                <div className="who-here-chips">
                  {opts.map((opt) => {
                    const on = liveAccessTier === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        className={`who-chip tier-chip${on ? " on" : ""}`}
                        aria-pressed={on}
                        onClick={() => handleSelectAccessTier(opt.id)}
                      >
                        {opt.displayName}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ===== The place's own questions (Song, Booth name, Row…): autosave ===== */}
          {customQuestions.length > 0 && (
            <CustomQuestionInputs
              questions={customQuestions}
              values={liveAnswers}
              status={answerStatus}
              onChange={handleLiveAnswerChange}
            />
          )}

          {/* ===== Timeline (universal): quick chips + inline time editing ===== */}
          <EventTimeline
            events={timelineEvents}
            onAddEvent={handleAddEvent}
            onRemoveEvent={handleRemoveEvent}
            onUpdateEvent={handleUpdateEvent}
            entityEventOptions={stepOptions}
            zone={zone}
            isFlight={isFlightActivity(interaction.customResponses as Record<string, unknown> | undefined)}
            quickActions={false}
          />

          {/* ===== Logged inside this place =====
                   A park check-in holds the day's rides; an airport holds the
                   lounge and the gate run. The list is the same timeline the
                   itinerary draws, so a place reads the same wherever you meet
                   it. */}
          <section className="nested-activities">
            <div className="section-header">
              <h2>
                Logged here
                {childActivities.length > 0 && (
                  <span className="nested-activities-count"> {childActivities.length}</span>
                )}
              </h2>
              <button
                type="button"
                className="btn btn-small btn-secondary"
                onClick={() => setLoggingInside(true)}
              >
                + Log activity here
              </button>
            </div>
            {descendantActivities.length > 0 ? (
              <ActivityList
                activities={descendantActivities}
                entityMap={relatedEntityMap}
                entityDataMap={relatedEntityDataMap}
                tripId={tripId!}
                onDay={segmentDayLookup(segments)}
                onZone={segmentZoneLookup(segments, trip?.timezone ?? undefined)}
              />
            ) : (
              <p className="text-muted">Nothing logged inside this yet.</p>
            )}
          </section>

          <LogActivitySheet
            open={loggingInside}
            onClose={() => setLoggingInside(false)}
            title={`Log inside ${interaction.name || entity?.displayName || "this activity"}`}
          >
            <LogActivityPicker
              tripId={tripId!}
              segments={segments}
              defaultSegmentId={interaction.segmentId ?? null}
              seedEntityIds={segmentLogSeeds(segments, tripInteractions, interaction.segmentId ?? null)}
              interactions={tripInteractions}
              parentInteractionId={interactionId}
            />
          </LogActivitySheet>

          {/* ===== Type module (only part that swaps): orders / rounds ===== */}
          {roundsModuleVisible && (
            <OrdersEditor
              orders={liveOrders}
              rounds={liveRounds}
              useRounds={liveUseRounds}
              onChange={handleLiveOrdersChange}
              travelParty={travelParty}
              participantIds={presentParticipants}
              statusIndicator={
                ordersStatus === "saving" ? (
                  <span className="live-notes-status"> Saving…</span>
                ) : ordersStatus === "saved" ? (
                  <span className="live-notes-status live-notes-status-saved"> Saved</span>
                ) : null
              }
            />
          )}

          {/* ===== Trivia module (live editing, like orders) ===== */}
          {isTrivia && (
            <div className="live-trivia">
              <div className="live-notes-header">
                <span className="live-notes-label">Trivia</span>
                {triviaStatus === "saving" && <span className="live-notes-status">Saving…</span>}
                {triviaStatus === "saved" && (
                  <span className="live-notes-status live-notes-status-saved">Saved</span>
                )}
              </div>
              <TriviaEditor
                questions={liveTriviaQuestions}
                score={liveTriviaScore}
                winningScore={liveTriviaWinningScore}
                onChange={handleLiveTriviaChange}
              />
            </div>
          )}

          {/* ===== Location / venue (universal): autosave ===== */}
          <div className="live-location">
            <div className="live-notes-header">
              <span className="live-notes-label">Location</span>
              {locationStatus === "saving" && <span className="live-notes-status">Saving…</span>}
              {locationStatus === "saved" && (
                <span className="live-notes-status live-notes-status-saved">Saved</span>
              )}
            </div>
            <input
              type="text"
              className="form-input"
              value={liveLocation}
              onChange={(e) => handleLiveLocationChange(e.target.value)}
              placeholder="e.g. Walt Disney Theatre"
            />
          </div>

          {/* ===== Notes (universal): autosave ===== */}
          <div className="live-notes">
            <div className="live-notes-header">
              <span className="live-notes-label">Notes</span>
              {notesStatus === "saving" && <span className="live-notes-status">Saving…</span>}
              {notesStatus === "saved" && <span className="live-notes-status live-notes-status-saved">Saved</span>}
            </div>
            <RichTextEditor
              value={liveNotes}
              onChange={handleLiveNotesChange}
              placeholder="Add notes..."
            />
          </div>

          {/* ===== Who was here (universal): trip party, inherited from the segment ===== */}
          <WhoWasThere
            travelParty={travelParty}
            selected={presentParticipants}
            onToggle={handleToggleParticipant}
            status={participantsStatus}
            hint={
              inheritsParticipants
                ? participantsSourceName
                  ? `Following ${participantsSourceName}. Tap anyone to set this activity's own group.`
                  : "Everyone on the trip. Tap anyone to set this activity's own group."
                : null
            }
          />

          {/* ===== Staff & crew (universal): activity-only roles ===== */}
          <ActivityPeople
            people={interaction.people || []}
            roleTypes={roles.all}
            featuredRoles={roles.featured}
            roleListEditing={
              canManageEntities && (effectiveType || entity)
                ? {
                    typeLabel: effectiveType ? entityTypeLabel(effectiveType) : null,
                    placeName: interaction.entityId && entity ? entity.displayName : null,
                    source: roles.source,
                    hasTypeList: typeRoles !== null,
                    hasPlaceList: placeRoles !== null,
                    onSave: handleSaveRoleList,
                  }
                : undefined
            }
            onAdd={handleAddPerson}
            onUpdate={handleUpdatePerson}
            onRemove={handleRemovePerson}
            openAddSignal={addPersonSignal}
          />
        </>
      )}
    </div>
  );
}
